import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react'
import useAddress from '@active/react-address-editor/components/useAddress'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import Form, { FormConsumer } from '@active/react-ui/Form'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import Dropdown from '@active/react-ui/Dropdown'
import MenuItemList from '@active/react-ui/MenuItemList'
import MenuItem from '@active/react-ui/MenuItem'
import IntlPhoneInput from '@active/react-ui/IntlPhoneInput'
import Input from '@active/react-ui/Input'
import CheckboxGroup from '@active/react-ui/CheckboxGroup'
import Checkbox from '@active/react-ui/Checkbox'
import isEmpty from 'lodash/isEmpty'
import { Locale, withI18n } from '@active/react-utils/i18n'
import { nameValidatorBuilder } from '../../shared/validator'
import pubSubInstance from '../../shared/utils/pubSub'
import { SHIPPING_ADDRESS_SUB_EVENT } from './utils'

const FIELD_PREFIX = 'addressForm.addressField'
const ONECOLFIELDS = ['line1', 'line2']

function ShippingAddressEditor(props) {
  const {
    fields,
    address,
    countries,
    onCountryChange,
  } = useAddress(props)
  const {
    name,
    i18n,
    fieldsConfig = {},
  } = props

  const id = `${FIELD_PREFIX}.country`
  const countryName = useMemo(() => `${name}.country`, [name])

  const nameValidate = useCallback(value => {
    const errMsg = nameValidatorBuilder(i18n, 'passport.error.validation.firstNamePrompt')(value)
    return new Promise(resolve => {
      errMsg ? resolve(errMsg) : resolve()
    })
  }, [i18n])

  return isEmpty(countries) ? null : (
    <Grid rowGap={20} container={false}>
      <GridItem span={12} spanMd={6}>
        <FormGroup key="firstName" name="firstName" required withoutLayout>
          <FormLabel htmlFor="firstName" truncate>
            <Locale id="passport.common.label.fname" />
          </FormLabel>
          <FormField
            as={Input}
            validate={nameValidate}
          />
        </FormGroup>
      </GridItem>
      <GridItem span={12} spanMd={6}>
        <FormGroup key="lastName" name="lastName" required withoutLayout>
          <FormLabel htmlFor="lastName" truncate>
            <Locale id="passport.common.label.lname" />
          </FormLabel>
          <FormField
            as={Input}
            validate={nameValidate}
          />
        </FormGroup>
      </GridItem>
      <GridItem span={12}>
        <FormGroup key="country" name={countryName} required withoutLayout>
          <FormLabel htmlFor="country" truncate><Locale id={id} /></FormLabel>
          <FormField
            as={Dropdown}
            onChange={onCountryChange}
            {...fieldsConfig.country}
          >
            <MenuItemList>
              {
                  countries.map(country => (
                    <MenuItem key={country.value} value={country.value}>{country.text}</MenuItem>
                  ))
                }
            </MenuItemList>
          </FormField>
        </FormGroup>
      </GridItem>
      {
          address.country
            ? fields.map(field => {
              const {
                name: fieldName,
                label,
                required: fieldRequired,
                component,
                data,
                ...fieldProps
              } = field
              const _fieldName = `${name}.${fieldName}`
              const isOneColField = ONECOLFIELDS.includes(fieldName)
              return (
                <GridItem span={12} spanMd={isOneColField ? 12 : 4}>
                  <FormGroup key={fieldName} name={_fieldName} required={fieldRequired} withoutLayout>
                    <FormLabel htmlFor={fieldName} truncate>
                      { label }
                    </FormLabel>
                    <FormField id={fieldName} as={component} {...fieldProps} {...fieldsConfig[fieldName]}>
                      {
                        data && (
                          <MenuItemList>
                            {
                              data.map(item => (
                                <MenuItem key={item.value} value={item.value}>{item.text}</MenuItem>
                              ))
                            }
                          </MenuItemList>
                        )
                      }
                    </FormField>
                  </FormGroup>
                </GridItem>
              )
            }) : null
        }
      <GridItem span={12}>
        <FormGroup key="phoneNumber" name="phoneNumber" withoutLayout>
          <FormLabel htmlFor="phoneNumber" truncate><Locale id="passport.user.userProfile.phoneNumber" /></FormLabel>
          <FormField
            as={IntlPhoneInput}
          />
        </FormGroup>
      </GridItem>
      <GridItem span={12}>
        <FormGroup key="isDefault" name="isDefault" withoutLayout>
          <FormField as={CheckboxGroup}>
            <Checkbox value="true">
              <Locale id="passport.shipping.address.set.default.checkbox.label" />
            </Checkbox>
          </FormField>
        </FormGroup>
      </GridItem>
    </Grid>
  )
}

function AddressForm({
  initialValues,
  ...rest
}) {
  const formRef = useRef()

  const handleSubmit = useCallback(() => {
    if (formRef.current) {
      return formRef.current.validateForm({ shouldTouchFields: true }).then(res => {
        const isFormValid = isEmpty(res)
        if (isFormValid) {
          const { values } = formRef.current
          return {
            status: 'SUCCESS',
            from: 'shipping-address-editor',
            values,
          }
        }
        return {
          status: 'FAILED',
          from: 'shipping-address-editor',
          message: 'form is invalid',
        }
      })
    }
    return {
      from: 'shipping-address-editor',
      status: 'FAILED',
      message: 'formRef.current is null',
    }
  }, [formRef.current])

  const getFormValues = useCallback(() => {
    if (formRef.current) {
      return {
        status: 'SUCCESS',
        from: 'shipping-address-editor',
        values: formRef.current.values,
      }
    }
    return {
      status: 'FAILED',
      from: 'shipping-address-editor',
      message: 'formRef.current is null',
    }
  }, [formRef.current])

  const handleFormValidation = useCallback(() => {
    if (formRef.current) {
      return formRef.current.validateForm({ shouldTouchFields: true }).then(res => {
        const isFormValid = isEmpty(res)
        return {
          status: isFormValid ? 'SUCCESS' : 'FAILED',
          from: 'shipping-address-editor',
        }
      })
    }
    return {
      status: 'SUCCESS',
    }
  }, [formRef.current])

  useEffect(() => {
    pubSubInstance.subscribe(SHIPPING_ADDRESS_SUB_EVENT.submit, handleSubmit)
    return () => {
      pubSubInstance.unsubscribe(SHIPPING_ADDRESS_SUB_EVENT.submit, handleSubmit)
    }
  }, [handleSubmit])

  useEffect(() => {
    pubSubInstance.subscribe(SHIPPING_ADDRESS_SUB_EVENT.getAddress, getFormValues)
    return () => {
      pubSubInstance.unsubscribe(SHIPPING_ADDRESS_SUB_EVENT.getAddress, getFormValues)
    }
  }, [getFormValues])

  useEffect(() => {
    pubSubInstance.subscribe(SHIPPING_ADDRESS_SUB_EVENT.validate, handleFormValidation)
    return () => {
      pubSubInstance.unsubscribe(SHIPPING_ADDRESS_SUB_EVENT.validate, handleFormValidation)
    }
  }, [handleFormValidation])

  return (
    <Form trimInput initialValues={initialValues}>
      <FormConsumer>
        {({ form }) => {
          const { values, setFieldValue, setFieldTouched } = form
          formRef.current = form
          return (
            <ShippingAddressEditor
              value={values.address}
              onFieldValueChange={setFieldValue}
              onFieldTouch={setFieldTouched}
              {...rest}
            />
          )
        }}
      </FormConsumer>
    </Form>
  )
}

export default withI18n(AddressForm)
